package ai.wagemix.web.components

import ai.wagemix.web.theme.WMBodyStrongTextStyle
import ai.wagemix.web.theme.WMCaptionTextStyle
import ai.wagemix.web.theme.WMCardCornerVar
import ai.wagemix.web.theme.WMControlSmHeightVar
import ai.wagemix.web.theme.WMHairlineVar
import ai.wagemix.web.theme.WMInlineGapVar
import ai.wagemix.web.theme.WMLabelStrongTextStyle
import ai.wagemix.web.theme.WMLabelTextStyle
import ai.wagemix.web.theme.WMListRowPaddingBlockVar
import ai.wagemix.web.theme.WMTabularTextStyle
import ai.wagemix.web.theme.WMTightGapVar
import androidx.compose.runtime.Composable
import com.fsryan.foundation.web.theme.FSMotionShortVar
import com.fsryan.foundation.web.theme.fs
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.textOverflow
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.whiteSpace
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.ComponentKind
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariant
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.CSSMediaQuery
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.StylePropertyValue
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.WhiteSpace
import org.jetbrains.compose.web.css.px

/** What an occupation row shows; a presentation model built from the domain by the view model. */
interface WMOccupationRowPresentation {
    val id: Int
    val name: String
    /** "SOC 11-9031 · Management"; `null` when the taxonomy has no code. */
    val caption: String?
    /** 0..100 */
    val weightPercent: Double
    val weightText: String
    val enabled: Boolean
    val locked: Boolean
    /**
     * `false` when the occupation is not part of the mix although it is enabled (the stats have no data for it): the
     * row is muted and its weight cannot be set, and its enable toggle still shows the user's choice.
     */
    val counted: Boolean

    companion object
}

fun WMOccupationRowPresentation.Companion.of(
    id: Int,
    name: String,
    caption: String?,
    weightPercent: Double,
    enabled: Boolean = true,
    locked: Boolean = false,
    counted: Boolean = true
): WMOccupationRowPresentation = WMOccupationRowPresentationData(
    id = id,
    name = name,
    caption = caption,
    weightPercent = weightPercent,
    weightText = WMFormat.percent(weightPercent),
    enabled = enabled,
    locked = locked,
    counted = counted
)

private data class WMOccupationRowPresentationData(
    override val id: Int,
    override val name: String,
    override val caption: String?,
    override val weightPercent: Double,
    override val weightText: String,
    override val enabled: Boolean,
    override val locked: Boolean,
    override val counted: Boolean
): WMOccupationRowPresentation

sealed interface WMOccupationRowKind: ComponentKind

val WMOccupationRowStyle = CssStyle<WMOccupationRowKind> {
    base {
        val colors = colorMode.toPalette().fs
        Modifier
            .fillMaxWidth()
            .padding(topBottom = WMListRowPaddingBlockVar.value())
            .gap(WMInlineGapVar.value())
            .borderBottom(width = WMHairlineVar.value(), style = LineStyle.Solid, color = colors.outlineVariant)
    }
}

/** A disabled occupation, or one that is not part of the mix, keeps its row but fades it. */
val WMMutedOccupationRowVariant = WMOccupationRowStyle.addVariant {
    base { Modifier.color(colorMode.toPalette().fs.onSurfaceVariant) }
}

/**
 * Penpot `Wagemix / Occupation Row` (Default | Locked): name and SOC caption, the weight,
 * a slider (or the "Upgrade to adjust weights" lock on free plans), a lock toggle to pin a
 * weight, an enable toggle and a remove button. All strings are parameters.
 */
@Composable
fun WMOccupationRow(
    row: WMOccupationRowPresentation,
    strings: WMOccupationRowStrings,
    modifier: Modifier = Modifier,
    weightsLocked: Boolean = false,
    onWeightChange: (Double) -> Unit = {},
    /** The weight slider was released; [onWeightChange] already saw every value on the way. */
    onWeightCommit: ((Double) -> Unit)? = null,
    onToggleLock: (() -> Unit)? = null,
    onToggleEnabled: ((Boolean) -> Unit)? = null,
    onRemove: (() -> Unit)? = null,
    onLockedWeightClick: (() -> Unit)? = null
) {
    val colors = ColorMode.current.toPalette().fs
    val muted = !row.enabled || !row.counted
    Column(
        modifier = WMOccupationRowStyle.toModifier(if (muted) WMMutedOccupationRowVariant else null)
            .role("listitem")
            .then(modifier)
    ) {
        Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()), verticalAlignment = Alignment.Top) {
            Column(modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value()).overflow(Overflow.Hidden)) {
                SpanText(row.name, WMBodyStrongTextStyle.toModifier())
                row.caption?.let { SpanText(it, WMCaptionTextStyle.toModifier().color(colors.onSurfaceVariant)) }
            }
            SpanText(
                row.weightText,
                WMLabelStrongTextStyle.toModifier().then(WMTabularTextStyle.toModifier()).minWidth(48.px).textAlign(TextAlign.End).flexShrink(0)
            )
            onToggleEnabled?.let { toggle ->
                WMToggle(checked = row.enabled, onCheckedChange = toggle, accessibilityString = strings.includeInMix)
            }
            onRemove?.let { remove ->
                WMIconButton(WMIconGlyph.X, accessibilityString = strings.remove, size = WMButtonSize.Sm, onClick = remove)
            }
        }
        if (weightsLocked) {
            Row(
                modifier = Modifier.fillMaxWidth().gap(WMTightGapVar.value()).color(colors.onSurfaceVariant)
                    .thenIf(onLockedWeightClick != null) { Modifier.cursor(Cursor.Pointer).onClick { onLockedWeightClick?.invoke() } }
                    .attrsModifier { attr("role", "button"); attr("aria-label", strings.upgradeToAdjust) },
                verticalAlignment = Alignment.CenterVertically
            ) {
                WMIcon(WMIconGlyph.Lock, accessibilityString = null, size = 16.px)
                SpanText(strings.upgradeToAdjust, WMCaptionTextStyle.toModifier())
            }
        } else {
            Row(modifier = Modifier.fillMaxWidth().gap(WMInlineGapVar.value()), verticalAlignment = Alignment.CenterVertically) {
                WMSlider(
                    value = row.weightPercent,
                    onValueChange = onWeightChange,
                    accessibilityString = "${strings.weightOf} ${row.name}",
                    enabled = row.enabled && row.counted && !row.locked,
                    onValueCommit = onWeightCommit
                )
                onToggleLock?.let { toggle ->
                    WMIconButton(
                        glyph = WMIconGlyph.Lock,
                        accessibilityString = if (row.locked) strings.unlockWeight else strings.lockWeight,
                        size = WMButtonSize.Sm,
                        kind = if (row.locked) WMButtonKind.Secondary else WMButtonKind.Ghost,
                        onClick = toggle
                    )
                }
            }
        }
    }
}

/** The strings an occupation row renders; the site passes them in. */
data class WMOccupationRowStrings(
    val includeInMix: String,
    val remove: String,
    val weightOf: String,
    val lockWeight: String,
    val unlockWeight: String,
    val upgradeToAdjust: String
)

/** What a row of the occupation picker shows. */
interface WMTreeRowPresentation {
    val id: Int
    val name: String
    val depth: Int
    /** The leaf occupations under this group (0 for a leaf); matches only when [countsMatches]. */
    val descendantCount: Int
    /** `true` while a search is active, so [descendantCount] counts the matching occupations. */
    val countsMatches: Boolean
    /** The row is a group that can expand. */
    val expandable: Boolean
    val expanded: Boolean
    val checked: Boolean
    val inMix: Boolean
    val parentSelected: Boolean

    companion object
}

fun WMTreeRowPresentation.Companion.of(
    id: Int,
    name: String,
    depth: Int,
    descendantCount: Int,
    countsMatches: Boolean,
    expandable: Boolean,
    expanded: Boolean,
    checked: Boolean,
    inMix: Boolean,
    parentSelected: Boolean,
): WMTreeRowPresentation {
    return WMTreeRowPresentationData(
        id = id,
        name = name,
        depth = depth,
        descendantCount = descendantCount,
        countsMatches = countsMatches,
        expandable = expandable,
        expanded = expanded,
        checked = checked,
        inMix = inMix,
        parentSelected = parentSelected,
    )
}

private data class WMTreeRowPresentationData(
    override val id: Int,
    override val name: String,
    override val depth: Int,
    override val descendantCount: Int,
    override val countsMatches: Boolean,
    override val expandable: Boolean,
    override val expanded: Boolean,
    override val checked: Boolean,
    override val inMix: Boolean,
    override val parentSelected: Boolean,
): WMTreeRowPresentation

val WMTreeRowStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(topBottom = WMTightGapVar.value(), leftRight = WMInlineGapVar.value())
            .gap(WMInlineGapVar.value())
            .borderRadius(WMCardCornerVar.value())
    }
    hover { Modifier.backgroundColor(colorMode.toPalette().fs.surfaceContainer) }
}

sealed interface WMTreeRowChevronKind: ComponentKind

/**
 * The group chevron points right when collapsed and turns to point down when expanded, with a
 * short transition that readers who prefer reduced motion do not get.
 */
val WMTreeRowChevronStyle = CssStyle<WMTreeRowChevronKind> {
    base {
        Modifier
            .flexShrink(0)
            .transition(Transition.of("transform", FSMotionShortVar.value()))
            .styleModifier { property("transform", "rotate(0deg)") }
    }
    cssRule(WMReducedMotion) { Modifier.styleModifier { property("transition", "none") } }
}

val WMExpandedTreeRowChevronVariant = WMTreeRowChevronStyle.addVariant {
    base { Modifier.styleModifier { property("transform", "rotate(90deg)") } }
}

/** The media feature of readers who ask for reduced motion. */
internal val WMReducedMotion = CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("reduce"))

/**
 * Penpot `Wagemix / Tree Row`: a row of the SOC hierarchy with a chevron button for groups (it
 * rotates to point down while expanded and reports `aria-expanded`), a checkbox, an "In mix" or
 * "Parent selected" tag and, for a group, the number of leaf occupations under it (the matching
 * ones while a search is active). Each depth level indents by one chevron width.
 */
@Composable
fun WMTreeRow(
    row: WMTreeRowPresentation,
    strings: WMTreeRowStrings,
    modifier: Modifier = Modifier,
    onToggleExpanded: () -> Unit = {},
    onCheckedChange: (Boolean) -> Unit = {}
) {
    val colors = ColorMode.current.toPalette().fs
    Row(
        modifier = WMTreeRowStyle.toModifier()
            .role("treeitem")
            .attrsModifier {
                attr("aria-level", "${row.depth + 1}")
                attr("aria-selected", "${row.checked}")
                if (row.expandable) {
                    attr("aria-expanded", "${row.expanded}")
                }
            }
            .styleModifier { property("padding-left", "calc(${row.depth} * ${WMControlSmHeightVar.value()})") }
            .then(modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        if (row.expandable) {
            WMIconButton(
                glyph = WMIconGlyph.ChevronRight,
                accessibilityString = if (row.expanded) strings.collapse else strings.expand,
                modifier = WMTreeRowChevronStyle.toModifier(if (row.expanded) WMExpandedTreeRowChevronVariant else null)
                    .attrsModifier { attr("aria-expanded", "${row.expanded}") },
                size = WMButtonSize.Sm,
                onClick = onToggleExpanded
            )
        } else {
            Row(modifier = Modifier.width(WMControlSmHeightVar.value()).flexShrink(0)) {}
        }
        WMCheckbox(
            checked = row.checked || row.inMix || row.parentSelected,
            onCheckedChange = onCheckedChange,
            enabled = !row.inMix && !row.parentSelected
        )
        SpanText(
            row.name,
            WMLabelTextStyle.toModifier()
                .fillMaxWidth()
                .whiteSpace(WhiteSpace.NoWrap)
                .overflow(Overflow.Hidden)
                .textOverflow(TextOverflow.Ellipsis)
        )
        when {
            row.inMix -> WMBadge(strings.inMix, kind = WMBadgeKind.Primary)
            row.parentSelected -> WMBadge(strings.parentSelected, kind = WMBadgeKind.Neutral)
        }
        if (row.descendantCount > 0) {
            val countLabel = if (row.countsMatches) strings.matchingOccupations(row.descendantCount) else strings.occupations(row.descendantCount)
            SpanText(
                "(${row.descendantCount})",
                WMCaptionTextStyle.toModifier()
                    .then(WMTabularTextStyle.toModifier())
                    .color(colors.onSurfaceVariant)
                    .flexShrink(0)
                    .attrsModifier {
                        attr("title", countLabel)
                        attr("aria-label", countLabel)
                    }
            )
        }
    }
}

data class WMTreeRowStrings(
    val expand: String,
    val collapse: String,
    val inMix: String,
    val parentSelected: String,
    /** A group's occupation count ("12 occupations", "1 occupation"). */
    val occupations: (count: Int) -> String,
    /** A group's count while a search is active ("3 matching occupations", "1 matching occupation"). */
    val matchingOccupations: (count: Int) -> String,
)
